// ReviewItemPanel — one review item (D86/D93) as a side panel: what is in
// doubt, the quotes it rests on, and the reviewer's call. A conflict is
// settled by picking or entering a value, an identity item by picking its
// target; either can be left blank with a reason. Accept is offered only where
// Specra can write the answer back (acceptBlocker), so an accepted item never
// writes nothing (D87).
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ReviewLineageKind, ReviewReasonCode } from '@mono/proto-clients';
import { SpecraBadge, SpecraButton, SpecraCombobox, SpecraFormField, SpecraInlineBanner, SpecraInput, SpecraKeyValueRow, SpecraOverlayDrawer, SpecraSectionHeader, SpecraSurface, SpecraText, SpecraTextarea, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useId, useMemo, useState } from 'react';
import { DocumentViewerDrawer } from '../../../../../components/ProposalDashboard/DocumentViewer/DocumentViewerDrawer';
import { useIsViewOnly } from '../../../../../contexts/authContext';
import { useWorkstreams } from '../../../Deliverables/hooks';
import { useResolveDiscrepancy, useSetDiscrepancyTargetPeriod } from '../../../PreAward/awardCompareHooks';
import { useContractCLINs } from '../../../Programs/contractHooks';
import { optionalTaskHooks, popHooks } from '../../../Programs/operationalHooks';
import { correctedFieldKind, correctedValueJson, isMoneyField, teamCorrectedValueJson } from '../../lib/correctedValue';
import { readableSnippet } from '../../lib/readableSnippet';
import { formatMoney, optionalTaskLabel } from '../../lib/recordLabels';
import { acceptBlocker, acknowledgesOnly, affectedLines, assignedRationale, badgeLabel, blankCopy, candidateKey, compactReason, contractTypeSubLines, decisionSummary, displayValue, enteredRationale, evidenceIsWhereWeLooked, fieldLabel, focusQuote, hasClosedChoices, isCheckOnly, isOccurrenceOwner, isReviewItem, isUnknownShape, itemSources, kindKey, leftBlankRationale, likelihood, lineIdForKey, nextStep, noEvidenceNote, occurrenceOwnerSummary, periodIdForKey, pickRationale, picksTarget, picksTeam, quoteNeedles, readableDays, recommendedCandidateKey, recordLabel, recordLabelFor, resolutionLabel, reviewChoices, reviewQuestion, reviewTitle, SETTLED_STATES, targetKind, taskIdForKey, todayISO, todayRationale, valueRows } from './reviewItemCopy';
// SpecraOverlayDrawer width "lg" is sm:max-w-2xl.
const DRAWER_WIDTH_PX = 672;
const OTHER = '__other__';
const NEW_TEAM = '__new_team__';
const LIVE_TEAM_PREFIX = 'live-team:';
// The states a code-26 team pick settles as (Accept sends Corrected).
const TEAM_PICKED_STATES = new Set([
    'Corrected',
    'Accepted',
    'Applied'
]);
// Code 17 is assembled server-side with reviewer-facing attribution.
export function displayReason(review) {
    if (review.reasonCode !== ReviewReasonCode.LATER_DOCUMENT_CONTRADICTS) {
        return readableDays(review.reason);
    }
    let reason = review.reason;
    if (isMoneyField(review.targetFieldPath)) {
        const esc = (v)=>v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Amounts sit only after "<field> " and "decided " ("P00005 states
        // funded_amount 600000 for 0101, but a reviewer decided 500000"); the
        // record locator after "for" is never rewritten.
        const lead = `(?<=(?:^|\\s)(?:${esc(review.targetFieldPath)}|decided) )`;
        for (const candidate of review.candidates){
            const raw = candidate.valueJson.trim();
            const amount = Number(raw);
            if (raw && Number.isFinite(amount)) {
                // Whole tokens only: 500000 must not rewrite the head of 5000001.
                reason = reason.replace(new RegExp(`${lead}${esc(raw)}(?!\\d|\\.\\d)`, 'g'), formatMoney(amount));
            }
        }
    }
    return reason;
}
// The reviewer's call on one item: the choice, blank-reason and busy state the
// panel body and the drawer footer share. Held by the drawer, keyed per item.
function useReviewDecision({ item, programId, changeSetId }) {
    const r = item.review;
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancy();
    const setTarget = useSetDiscrepancyTargetPeriod();
    const [choice, setChoice] = useState(()=>recommendedCandidateKey(r));
    const [typed, setTyped] = useState(()=>picksTeam(r) ? r.candidates.find((candidate)=>!candidate.workstreamId)?.label ?? '' : '');
    const [note, setNote] = useState('');
    // Set only by the "Use today's date" suggestion, so the rationale can say so.
    const [usedToday, setUsedToday] = useState(false);
    const [leavingBlank, setLeavingBlank] = useState(false);
    const [blankReason, setBlankReason] = useState('');
    // The team this reviewer just picked. The settled item's wire shape carries
    // no decided value, so only an in-session pick can be named.
    const [decidedTeam, setDecidedTeam] = useState('');
    const settled = SETTLED_STATES.has(item.resolutionState);
    const isIdentity = picksTarget(r);
    const isTeam = picksTeam(r);
    const blocker = settled ? null : acceptBlocker(item);
    const canAct = !settled && !isViewOnly;
    const kind = isIdentity && !blocker ? targetKind(r.targetObjectType) : null;
    // Pre-Award has no program yet, so there are no rows to pick from. Periods
    // load for every period item, settled ones included: the record is named by
    // its row (recordLabelFor), not by the key pangaea derived.
    const { data: periodsResp } = popHooks.useList({
        programId
    }, {
        enabled: targetKind(r.targetObjectType) === 'period' && !!programId
    });
    // A line's contract type is written to its untyped sub-lines too, so their
    // lines load to say how many (contractTypeSubLines).
    const { data: linesResp } = useContractCLINs(kind === 'line' || hasClosedChoices(r) ? programId : null);
    const { data: tasksResp } = optionalTaskHooks.useList({
        programId
    }, {
        enabled: kind === 'task' && !!programId
    });
    const workstreams = useWorkstreams(programId, {
        enabled: isTeam && !!programId
    });
    const periods = periodsResp?.items ?? [];
    const lines = linesResp?.clins ?? [];
    const tasks = tasksResp?.items ?? [];
    const field = r.targetFieldPath;
    const fieldKind = correctedFieldKind(field);
    const busy = resolve.isPending || setTarget.isPending;
    const choices = useMemo(()=>reviewChoices(r), [
        r
    ]);
    const subLines = contractTypeSubLines(r, lines);
    const acknowledge = acknowledgesOnly(item);
    // An identity item's targets: natural keys mapped to the rows
    // SetDiscrepancyTargetPeriod takes.
    const rowIdForKey = (key)=>{
        if (kind === 'line') return lineIdForKey(key, lines);
        if (kind === 'task') return taskIdForKey(key, tasks);
        return periodIdForKey(key, periods);
    };
    const everyRow = ()=>{
        if (kind === 'line') {
            return lines.map((l)=>({
                    value: `line:${l.id}`,
                    label: `CLIN ${l.clinNumber}`,
                    hint: l.shortTitle,
                    rowId: l.id
                }));
        }
        if (kind === 'task') {
            return tasks.map((t)=>({
                    value: `task:${t.id}`,
                    label: optionalTaskLabel(String(t.taskNumber)),
                    hint: t.name,
                    rowId: t.id
                }));
        }
        return periods.map((p)=>({
                value: `period:${p.id}`,
                label: p.periodName,
                hint: '',
                rowId: p.id
            }));
    };
    // The emitter's guesses lead; every other row of the kind follows, so a
    // wrong or unresolvable guess never hides the right target.
    const guessed = isIdentity ? r.candidates.map((c)=>({
            value: c.key,
            label: c.label || candidateKey(c),
            hint: likelihood(c.score),
            rowId: rowIdForKey(candidateKey(c))
        })) : [];
    const targets = isIdentity ? [
        ...guessed,
        ...everyRow().filter((o)=>!guessed.some((g)=>g.rowId && g.rowId === o.rowId))
    ] : [];
    // What Accept would send, or why it can't yet.
    const pickedCandidate = isIdentity ? undefined : choices.find((c)=>c.key === choice);
    const pickedLiveTeam = isTeam && choice.startsWith(LIVE_TEAM_PREFIX) ? workstreams.data?.find((team)=>team.id === choice.slice(LIVE_TEAM_PREFIX.length)) : undefined;
    const pickedTarget = targets.find((t)=>t.value === choice);
    const typedJson = isTeam ? choice === NEW_TEAM ? teamCorrectedValueJson({
        newTeamName: typed
    }) : pickedLiveTeam ? teamCorrectedValueJson({
        workstreamId: pickedLiveTeam.id
    }) : pickedCandidate?.workstreamId ? teamCorrectedValueJson({
        workstreamId: pickedCandidate.workstreamId
    }) : pickedCandidate ? teamCorrectedValueJson({
        newTeamName: pickedCandidate.label
    }) : '' : choice === OTHER ? correctedValueJson(field, typed) : '';
    const targetRowId = pickedTarget?.rowId ?? '';
    const acceptReady = acknowledge ? true : isIdentity ? !!targetRowId : isTeam ? typedJson !== '' : !!pickedCandidate || choice === OTHER && typedJson !== '';
    const pickFirst = kind === 'line' ? 'Pick the line this belongs to first' : kind === 'task' ? 'Pick the optional task this belongs to first' : 'Pick the period this belongs to first';
    const acceptWhyNot = isViewOnly ? 'You have view-only access' : !acceptReady ? isIdentity ? pickFirst : 'Pick a value, or enter one, first' : '';
    // Failures are already toasted by the mutation hooks; the panel stays open.
    const onAccept = async ()=>{
        if (!acceptReady) return;
        try {
            await accept();
        } catch  {
        /* toasted */ }
    };
    const accept = async ()=>{
        if (acknowledge) {
            // Recorded and acknowledged on apply; nothing is written.
            await resolve.mutateAsync({
                changeSetId,
                discrepancyId: item.id,
                resolutionState: 'Accepted',
                decisionRationale: note.trim() ? `Acknowledged. ${note.trim()}` : 'Acknowledged'
            });
        } else if (isIdentity && pickedTarget) {
            // The target first: an accepted identity item with no target writes
            // nothing. Its value is the item's own proposed value.
            await setTarget.mutateAsync(kind === 'period' ? {
                changeSetId,
                discrepancyId: item.id,
                periodId: targetRowId
            } : {
                changeSetId,
                discrepancyId: item.id,
                targetObjectId: targetRowId
            });
            await resolve.mutateAsync({
                changeSetId,
                discrepancyId: item.id,
                resolutionState: 'Accepted',
                decisionRationale: assignedRationale(pickedTarget.label, note),
                decidedValueJson: item.proposedValueJson
            });
        } else if (isTeam) {
            await resolve.mutateAsync({
                changeSetId,
                discrepancyId: item.id,
                resolutionState: 'Corrected',
                decisionRationale: pickedCandidate ? pickRationale(pickedCandidate, note) : enteredRationale(pickedLiveTeam?.name ?? typed.trim(), note),
                decidedValueJson: typedJson
            });
            setDecidedTeam(choice === NEW_TEAM ? typed.trim() : pickedLiveTeam?.name ?? pickedCandidate?.label ?? '');
        } else if (pickedCandidate) {
            await resolve.mutateAsync({
                changeSetId,
                discrepancyId: item.id,
                resolutionState: 'Accepted',
                decisionRationale: pickRationale(pickedCandidate, note, field),
                decidedValueJson: pickedCandidate.valueJson
            });
        } else {
            await resolve.mutateAsync({
                changeSetId,
                discrepancyId: item.id,
                resolutionState: 'Corrected',
                decisionRationale: usedToday && typed === todayISO() ? todayRationale(typed, note) : enteredRationale(typed.trim(), note),
                decidedValueJson: typedJson
            });
        }
    };
    // Recorded as Waived; the reviewer only ever reads "Leave blank". Never
    // writes a value, today's date included.
    const onLeaveBlank = async ()=>{
        const reason = blankReason.trim();
        if (!reason) return;
        try {
            await resolve.mutateAsync({
                changeSetId,
                discrepancyId: item.id,
                resolutionState: 'Waived',
                decisionRationale: leftBlankRationale(reason, item.review),
                ...blankCopy(item.review).toast ? {
                    savedToast: blankCopy(item.review).toast
                } : {}
            });
        } catch  {
        /* toasted */ }
    };
    return {
        choice,
        setChoice,
        typed,
        setTyped,
        note,
        setNote,
        setUsedToday,
        leavingBlank,
        setLeavingBlank,
        blankReason,
        setBlankReason,
        settled,
        isIdentity,
        isTeam,
        blocker,
        canAct,
        targets,
        periods,
        workstreams: workstreams.data ?? [],
        field,
        fieldKind,
        busy,
        acceptReady,
        acceptWhyNot,
        onAccept,
        onLeaveBlank,
        decidedTeam,
        blank: blankCopy(r),
        choices,
        subLines,
        acknowledge,
        pickedCandidate
    };
}
export function ReviewItemPanel({ item, decision, projectId, fallbackDocId = '', fallbackDocLabel = 'Source document' }) {
    const r = item.review;
    const { choice, setChoice, typed, setTyped, note, setNote, setUsedToday, settled, isIdentity, isTeam, blocker, canAct, targets, periods, workstreams, field, fieldKind } = decision;
    const [showFullReason, setShowFullReason] = useState(false);
    const [viewing, setViewing] = useState(null);
    const affected = affectedLines(r);
    const occurrenceOwner = isOccurrenceOwner(r);
    const valueInputId = useId();
    const openSource = (s)=>{
        const docId = s.artifactId || fallbackDocId;
        if (!docId || !projectId) return;
        setViewing({
            docId,
            page: s.page > 0 ? s.page : 1,
            blocks: [
                ...s.elementIds
            ]
        });
    };
    const record = recordLabelFor(item, periods);
    const reason = compactReason(displayReason(r));
    const sources = itemSources(item, decision.choices);
    const needles = quoteNeedles(r, [
        item.proposedValueJson,
        item.decidedValueJson ?? ''
    ]);
    const lookedOnly = evidenceIsWhereWeLooked(r);
    const subLines = decision.subLines;
    const step = nextStep(r);
    // A later document that settled this item, with the passage it settled it by.
    const settledBySource = r.lineage.find((l)=>l.kind === ReviewLineageKind.SETTLED && l.sources.length > 0);
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.reviewItem.panel,
        "data-item-id": item.id,
        "data-kind": kindKey(r.kind),
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-[var(--space-3-5)] flex flex-wrap gap-[var(--space-1-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: isIdentity || occurrenceOwner ? 'info' : 'warning',
                        "data-testid": Testids.reviewItem.kindBadge,
                        "data-kind": kindKey(r.kind),
                        children: badgeLabel(r)
                    }),
                    settled ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "success",
                        children: resolutionLabel(item.resolutionState, r)
                    }) : decision.acknowledge ? /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "neutral",
                        children: "Acknowledge to continue"
                    }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: "danger",
                        children: "Blocks commit"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("h3", {
                className: "text-[length:var(--fs-ui-title)] font-medium leading-snug text-foreground",
                "data-testid": Testids.reviewItem.title,
                "data-reason-code": r.reasonCode,
                children: reviewTitle(r)
            }),
            r.reason && /*#__PURE__*/ _jsxs("p", {
                className: "mt-1 text-[length:var(--fs-ui-body)] leading-[1.6] text-muted-foreground",
                "data-testid": Testids.reviewItem.reason,
                children: [
                    showFullReason ? reason.full : reason.short,
                    reason.collapsed && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            ' ',
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                variant: "link",
                                size: "sm",
                                className: "h-auto p-0",
                                onClick: ()=>setShowFullReason((v)=>!v),
                                children: showFullReason ? 'Show less' : 'Show all'
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        headingAs: "h4",
                        title: "What’s in doubt"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2 space-y-1",
                        "data-testid": Testids.reviewItem.target,
                        "data-target-type": r.targetObjectType,
                        "data-locator": r.targetLocator,
                        "data-field": field,
                        children: [
                            /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Record",
                                value: record
                            }),
                            occurrenceOwner ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Due",
                                value: occurrenceOwnerSummary(r).dates
                            }) : /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                                label: "Field",
                                value: fieldLabel(field)
                            }),
                            isIdentity && item.proposedValueJson && /*#__PURE__*/ _jsx(ReviewValue, {
                                json: item.proposedValueJson,
                                field: field
                            })
                        ]
                    }),
                    subLines.length > 0 && /*#__PURE__*/ _jsxs("details", {
                        className: "mt-3",
                        "data-testid": Testids.reviewItem.appliesTo,
                        "data-count": subLines.length,
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "summary",
                                className: "cursor-pointer",
                                size: "body-ui",
                                children: [
                                    "Applies to ",
                                    record,
                                    " + ",
                                    subLines.length,
                                    " sub-line",
                                    subLines.length === 1 ? '' : 's',
                                    ' ',
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "(show)"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: "mt-1 flex flex-wrap gap-1",
                                children: subLines.map((l)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "neutral",
                                            children: l
                                        })
                                    }, l))
                            })
                        ]
                    }),
                    affected.length > 0 && /*#__PURE__*/ _jsxs("details", {
                        className: "mt-3",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "summary",
                                className: "cursor-pointer",
                                size: "meta",
                                tone: "muted",
                                children: [
                                    "Also applies to ",
                                    affected.length,
                                    " sub-line",
                                    affected.length === 1 ? '' : 's',
                                    ". Settling this settles ",
                                    affected.length === 1 ? 'it' : 'them',
                                    " too. (show)"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("ul", {
                                className: "mt-1 flex flex-wrap gap-1",
                                children: affected.map((l)=>/*#__PURE__*/ _jsx("li", {
                                        children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                            variant: "neutral",
                                            "data-testid": Testids.reviewItem.affectedLine,
                                            "data-locator": l,
                                            children: recordLabel(r.targetObjectType, l)
                                        })
                                    }, l))
                            })
                        ]
                    })
                ]
            }),
            !occurrenceOwner && (sources.length > 0 || !decision.choices.some((c)=>c.sources.length > 0)) && /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        headingAs: "h4",
                        title: lookedOnly ? 'Where we looked' : 'What the documents say'
                    }),
                    sources.length > 0 ? /*#__PURE__*/ _jsx("div", {
                        className: "mt-2 space-y-2",
                        children: sources.map((s, i)=>/*#__PURE__*/ _jsx(ReviewQuote, {
                                source: s,
                                needles: needles,
                                caption: lookedOnly ? `No ${fieldLabel(field).toLowerCase()} appears in this passage.` : undefined,
                                fallbackDocId: fallbackDocId,
                                canOpen: !!projectId,
                                onOpen: openSource
                            }, `src-${i}`))
                    }) : /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "mt-2",
                        size: "body-ui",
                        tone: "muted",
                        "data-testid": Testids.reviewItem.noEvidence,
                        children: noEvidenceNote(r)
                    })
                ]
            }),
            !settled && /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        headingAs: "h4",
                        title: "Your call"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[length:var(--fs-ui-body)] font-medium text-foreground",
                        "data-testid": Testids.reviewItem.question,
                        children: occurrenceOwner ? 'Which team filed this?' : isTeam ? 'Which team does this line belong to?' : reviewQuestion(r, record)
                    }),
                    step && (decision.acknowledge || isCheckOnly(r)) && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "mt-2",
                        size: "body-ui",
                        "data-testid": Testids.reviewItem.nextStep,
                        children: step
                    }),
                    blocker && !isCheckOnly(r) && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "warning",
                        className: "mt-2",
                        "data-testid": blocker.kind === 'targetUnavailable' ? Testids.reviewItem.targetUnavailable : Testids.reviewItem.noSettleWrite,
                        "data-target-type": r.targetObjectType,
                        "data-field": field,
                        children: blocker.message
                    }),
                    blocker && isCheckOnly(r) && !step && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "mt-2",
                        size: "meta",
                        tone: "muted",
                        "data-testid": Testids.reviewItem.noSettleWrite,
                        "data-target-type": r.targetObjectType,
                        "data-field": field,
                        children: blocker.message
                    }),
                    decision.acknowledge ? null : isTeam && !blocker ? /*#__PURE__*/ _jsx(TeamPicker, {
                        candidates: r.candidates,
                        field: field,
                        teams: workstreams,
                        choice: choice,
                        name: typed,
                        interactive: canAct,
                        onChoose: setChoice,
                        onNameChange: setTyped,
                        fallbackDocId: fallbackDocId,
                        canOpen: !!projectId,
                        onOpen: openSource
                    }) : isIdentity && !blocker ? /*#__PURE__*/ _jsx("div", {
                        className: "mt-2",
                        "data-testid": Testids.reviewItem.targetPicker,
                        children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                            label: "Belongs to",
                            placeholder: "Choose where this belongs…",
                            disabled: !canAct,
                            value: choice || undefined,
                            onValueChange: (v)=>setChoice(v ?? ''),
                            options: targets.map((t)=>({
                                    value: t.value,
                                    label: t.label,
                                    description: t.hint || undefined,
                                    disabled: !t.rowId
                                }))
                        })
                    }) : /*#__PURE__*/ _jsx(CandidateList, {
                        candidates: decision.choices,
                        field: field,
                        choice: choice,
                        interactive: canAct && !blocker,
                        onChoose: setChoice,
                        fallbackDocId: fallbackDocId,
                        canOpen: !!projectId,
                        onOpen: openSource,
                        offerOther: !blocker && !hasClosedChoices(r),
                        needles: needles
                    }),
                    !isIdentity && !isTeam && !blocker && !decision.acknowledge && !hasClosedChoices(r) && (choice === OTHER || r.candidates.length === 0) && /*#__PURE__*/ _jsx(SpecraFormField, {
                        className: "mt-3",
                        htmlFor: valueInputId,
                        label: r.candidates.length === 0 ? 'Enter the value from the document' : 'Your value',
                        children: /*#__PURE__*/ _jsxs("span", {
                            className: "flex items-center gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: valueInputId,
                                    className: "flex-1",
                                    type: fieldKind,
                                    step: fieldKind === 'number' ? 'any' : undefined,
                                    value: typed,
                                    disabled: !canAct,
                                    onChange: (e)=>{
                                        setTyped(e.target.value);
                                        setUsedToday(false);
                                        if (r.candidates.length === 0) setChoice(OTHER);
                                    },
                                    "data-testid": Testids.reviewItem.valueInput,
                                    "data-field-kind": fieldKind
                                }),
                                fieldKind === 'date' && // A suggestion only: it fills the input, and the reviewer
                                // still has to press Accept. A date is never defaulted.
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "button",
                                    variant: "secondary",
                                    size: "sm",
                                    disabled: !canAct,
                                    onClick: ()=>{
                                        setTyped(todayISO());
                                        setUsedToday(true);
                                        setChoice(OTHER);
                                    },
                                    "data-testid": Testids.reviewItem.useTodayButton,
                                    children: "Use today’s date"
                                })
                            ]
                        })
                    }),
                    !blocker && /*#__PURE__*/ _jsx(SpecraFormField, {
                        className: "mt-3",
                        label: "Note (optional)",
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: note,
                            disabled: !canAct,
                            onChange: (e)=>setNote(e.target.value),
                            placeholder: "Added to the recorded reason",
                            "data-testid": Testids.reviewItem.note
                        })
                    })
                ]
            }),
            settled && /*#__PURE__*/ _jsxs("section", {
                className: "mt-[var(--space-5-5)]",
                "data-testid": Testids.reviewItem.decision,
                "data-resolution": item.resolutionState,
                children: [
                    /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                        variant: "group",
                        headingAs: "h4",
                        title: "Decision"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[length:var(--fs-ui-body)] text-foreground",
                        children: occurrenceOwner && TEAM_PICKED_STATES.has(item.resolutionState) ? `Filed by ${decision.decidedTeam || 'the team picked for it'}.${item.resolutionState === 'Applied' ? '' : ' Will apply when committed.'}` : decisionSummary(item, {
                            record,
                            subLines: subLines.length
                        })
                    }),
                    settledBySource && /*#__PURE__*/ _jsx("div", {
                        className: "mt-2 space-y-2",
                        children: settledBySource.sources.map((s, i)=>/*#__PURE__*/ _jsx(ReviewQuote, {
                                source: s,
                                needles: needles,
                                fallbackDocId: fallbackDocId,
                                canOpen: !!projectId,
                                onOpen: openSource
                            }, `settled-${i}`))
                    })
                ]
            }),
            viewing && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: true,
                onClose: ()=>setViewing(null),
                projectId: projectId,
                document: {
                    documentId: viewing.docId,
                    filename: viewing.docId === fallbackDocId ? fallbackDocLabel : 'Source document'
                },
                initialPage: viewing.page,
                highlightBlockIds: viewing.blocks,
                // The panel's drawer is a Radix dialog; extension mode sits beside it
                // above its scrim (the OptionalTaskExercisesDrawer pattern).
                mode: "extension",
                parentDrawerWidth: DRAWER_WIDTH_PX
            })
        ]
    });
}
function ReviewQuote({ source, needles = [], caption, fallbackDocId, canOpen, onOpen }) {
    const docId = source.artifactId || fallbackDocId;
    const page = source.page > 0 ? source.page : 0;
    const full = readableSnippet(source.quote);
    const focused = focusQuote(full, needles);
    const boxed = source.elementIds.length > 0;
    return /*#__PURE__*/ _jsxs(SpecraSurface, {
        radius: "md",
        className: "p-2.5",
        "data-testid": Testids.reviewItem.sourceQuote,
        "data-page": page || undefined,
        "data-artifact-id": docId || undefined,
        "data-boxed": boxed || undefined,
        children: [
            full && /*#__PURE__*/ _jsxs("blockquote", {
                className: "whitespace-pre-line border-l-2 border-border pl-2 text-[length:var(--fs-ui-body)] italic text-muted-foreground",
                children: [
                    "“",
                    focused.cut ? `… ${focused.text} …` : focused.text,
                    "”"
                ]
            }),
            focused.cut && /*#__PURE__*/ _jsxs("details", {
                className: "mt-1",
                children: [
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "summary",
                        className: "cursor-pointer",
                        size: "meta",
                        tone: "muted",
                        children: "Full passage"
                    }),
                    /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "mt-1 whitespace-pre-line",
                        size: "meta",
                        tone: "muted",
                        children: full
                    })
                ]
            }),
            caption && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "mt-1",
                size: "meta",
                children: caption
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-1.5 flex items-center justify-between gap-2 text-[length:var(--fs-ui-meta)] text-muted-foreground",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: page ? `p. ${page}` : 'Page not recorded'
                    }),
                    canOpen && docId && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        onClick: ()=>onOpen(source),
                        "data-testid": Testids.reviewItem.sourceOpen,
                        "data-page": page || 1,
                        children: page ? `Open page ${page}` : 'Open document'
                    })
                ]
            })
        ]
    });
}
// A structured value as the reviewer reads it: a labelled row per fact where
// the shape is known, the raw JSON behind a Details toggle where it is not.
function ReviewValue({ json, field }) {
    const rows = valueRows(json);
    if (rows) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: rows.map((row)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                    label: row.label,
                    value: row.value,
                    variant: row.label === 'Funding change' ? 'numeric' : undefined,
                    "data-testid": Testids.reviewItem.valueRow
                }, row.label))
        });
    }
    if (isUnknownShape(json, field)) {
        return /*#__PURE__*/ _jsxs("details", {
            className: "py-[var(--space-2)]",
            "data-testid": Testids.reviewItem.valueDetails,
            children: [
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "summary",
                    className: "cursor-pointer",
                    size: "body-ui",
                    tone: "muted",
                    children: "Details"
                }),
                /*#__PURE__*/ _jsx(SpecraText, {
                    as: "pre",
                    className: "mt-[var(--space-1)] whitespace-pre-wrap break-words",
                    size: "meta",
                    tone: "muted",
                    children: JSON.stringify(JSON.parse(json), null, 2)
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
        label: "Value",
        value: displayValue(json, field)
    });
}
function TeamPicker({ candidates, field, teams, choice, name, interactive, onChoose, onNameChange, fallbackDocId, canOpen, onOpen }) {
    const selectedTeam = teams.find((team)=>choice === `${LIVE_TEAM_PREFIX}${team.id}`);
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-2 space-y-3",
        "data-testid": Testids.reviewItem.teamPicker,
        children: [
            /*#__PURE__*/ _jsx(CandidateList, {
                candidates: candidates,
                field: field,
                choice: choice,
                interactive: interactive,
                onChoose: onChoose,
                fallbackDocId: fallbackDocId,
                canOpen: canOpen,
                onOpen: onOpen,
                offerOther: false,
                hideValues: true
            }),
            /*#__PURE__*/ _jsxs(SpecraSurface, {
                radius: "md",
                className: "space-y-2 p-2.5",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "secondary",
                        size: "sm",
                        disabled: !interactive,
                        onClick: ()=>onChoose(NEW_TEAM),
                        "data-testid": Testids.reviewItem.newTeam,
                        "aria-pressed": choice === NEW_TEAM,
                        children: "New team"
                    }),
                    choice === NEW_TEAM && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Team name",
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            value: name,
                            disabled: !interactive,
                            onChange: (event)=>onNameChange(event.target.value),
                            "data-testid": Testids.reviewItem.newTeamName
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                "data-testid": Testids.reviewItem.teamSearch,
                children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                    label: "Every team on this program",
                    placeholder: "Choose a team…",
                    searchPlaceholder: "Search teams…",
                    disabled: !interactive,
                    value: selectedTeam ? `${LIVE_TEAM_PREFIX}${selectedTeam.id}` : undefined,
                    onValueChange: (value)=>onChoose(value ?? ''),
                    options: teams.map((team)=>({
                            value: `${LIVE_TEAM_PREFIX}${team.id}`,
                            label: team.name,
                            description: team.aliases.length ? `Also called: ${team.aliases.join(', ')}` : undefined,
                            keywords: team.aliases
                        }))
                })
            }),
            selectedTeam?.aliases.length ? /*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                label: "Also called",
                value: selectedTeam.aliases.join(', ')
            }) : /*#__PURE__*/ _jsx(SpecraText, {
                size: "meta",
                tone: "muted",
                children: "Search every live team by name or alias."
            })
        ]
    });
}
function CandidateList({ candidates, field, choice, interactive, onChoose, fallbackDocId, canOpen, onOpen, offerOther, hideValues = false, needles = [] }) {
    if (candidates.length === 0) return null;
    const row = (key, label, value, sources)=>{
        const pressed = choice === key;
        return /*#__PURE__*/ _jsxs("div", {
            className: `rounded-[var(--radius-md)] border p-2.5 ${pressed ? 'border-brand bg-brand-muted' : 'border-border/70 bg-background'}`,
            children: [
                /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    className: "flex w-full items-baseline justify-between gap-2 text-left disabled:cursor-default",
                    "aria-pressed": pressed,
                    disabled: !interactive,
                    onClick: ()=>onChoose(key),
                    "data-testid": Testids.reviewItem.candidate,
                    "data-candidate-key": key,
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "text-[length:var(--fs-ui-body)] font-medium text-foreground",
                            children: label
                        }),
                        value && /*#__PURE__*/ _jsx("span", {
                            className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                            children: value
                        })
                    ]
                }),
                sources.length > 0 && /*#__PURE__*/ _jsx("div", {
                    className: "mt-2 space-y-1.5",
                    children: sources.map((s, i)=>/*#__PURE__*/ _jsx(ReviewQuote, {
                            source: s,
                            needles: needles,
                            fallbackDocId: fallbackDocId,
                            canOpen: canOpen,
                            onOpen: onOpen
                        }, `${key}-src-${i}`))
                })
            ]
        }, key);
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-2 space-y-2",
        children: [
            candidates.map((c)=>/*#__PURE__*/ _jsxs("div", {
                    children: [
                        row(c.key, c.label || (hideValues ? 'Unnamed team' : displayValue(c.valueJson, field)), !hideValues && !isUnknownShape(c.valueJson, field) && c.label && !c.label.includes(displayValue(c.valueJson, field)) ? displayValue(c.valueJson, field) : '', c.sources),
                        isUnknownShape(c.valueJson, field) && /*#__PURE__*/ _jsx(ReviewValue, {
                            json: c.valueJson,
                            field: field
                        })
                    ]
                }, c.key)),
            offerOther && interactive && row(OTHER, 'Enter a different value', '', [])
        ]
    });
}
// ReviewItemDrawer is the side panel shell a surface mounts: it holds only the
// item's id and derives the item from the list it is given, so a refetch after
// a resolve shows the settled item instead of a stale copy. `open` is separate
// from the id so the panel keeps its content while the drawer animates shut,
// instead of emptying to a blank shell first.
export function ReviewItemDrawer({ items, openId, open, onClose, ...rest }) {
    const found = openId ? items.find((d)=>d.id === openId) : undefined;
    const shown = found && isReviewItem(found) ? found : undefined;
    const onOpenChange = (next)=>{
        if (!next) onClose();
    };
    if (!shown) {
        return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
            open: false,
            onOpenChange: onOpenChange,
            title: "",
            testId: Testids.reviewItem.drawer,
            width: "lg",
            children: null
        });
    }
    // Keyed per item, so the reviewer's call never carries over to another item.
    return /*#__PURE__*/ _jsx(ReviewItemDrawerOpen, {
        item: shown,
        open: open,
        onOpenChange: onOpenChange,
        ...rest
    }, shown.id);
}
function ReviewItemDrawerOpen({ item, open, onOpenChange, programId, projectId, changeSetId, fallbackDocId, fallbackDocLabel }) {
    const decision = useReviewDecision({
        item,
        programId,
        changeSetId
    });
    const fundingTitle = item.review.targetFieldPath === 'funded_amount_contribution';
    return /*#__PURE__*/ _jsx(SpecraOverlayDrawer, {
        open: open,
        onOpenChange: onOpenChange,
        eyebrow: fundingTitle ? undefined : recordLabelFor(item, decision.periods),
        title: fundingTitle ? `${recordLabelFor(item, decision.periods)} · Funding` : 'Needs your call',
        testId: Testids.reviewItem.drawer,
        width: "lg",
        footer: decision.settled ? undefined : /*#__PURE__*/ _jsx(ReviewItemActions, {
            decision: decision
        }),
        children: /*#__PURE__*/ _jsx(ReviewItemPanel, {
            item: item,
            decision: decision,
            projectId: projectId,
            fallbackDocId: fallbackDocId,
            fallbackDocLabel: fallbackDocLabel
        })
    });
}
// What Accept writes, on the button itself: "Set FFP on 37 lines".
function acceptLabel(decision) {
    if (decision.acknowledge) return 'Acknowledge';
    if (decision.isIdentity) return 'Assign and accept';
    const picked = decision.pickedCandidate;
    if (picked && decision.subLines.length > 0) {
        const value = displayValue(picked.valueJson, decision.field);
        return `Set ${value} on ${decision.subLines.length + 1} lines`;
    }
    return 'Use this value';
}
// The drawer footer: Leave blank (with its reason) and Accept.
function ReviewItemActions({ decision }) {
    const { leavingBlank, setLeavingBlank, blankReason, setBlankReason, canAct, busy, blocker, acceptReady, acceptWhyNot, onAccept, onLeaveBlank, blank, acknowledge } = decision;
    return /*#__PURE__*/ _jsxs("footer", {
        className: "shrink-0 space-y-2 border-t border-border bg-muted px-4 py-3 sm:px-5",
        children: [
            leavingBlank && /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-[length:var(--fs-ui-meta)] text-muted-foreground",
                        children: blank.hint
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: blank.prompt,
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: blankReason,
                            onChange: (e)=>setBlankReason(e.target.value),
                            placeholder: blank.placeholder,
                            "data-testid": Testids.reviewItem.leaveBlankReason
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    leavingBlank ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "destructive",
                                size: "sm",
                                disabled: !blankReason.trim() || !canAct || busy,
                                onClick: ()=>void onLeaveBlank(),
                                "data-testid": Testids.reviewItem.leaveBlankSubmit,
                                children: blank.action
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                size: "sm",
                                onClick: ()=>setLeavingBlank(false),
                                children: "Cancel"
                            })
                        ]
                    }) : acknowledge ? null : /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "destructive",
                        size: "sm",
                        disabled: !canAct,
                        onClick: ()=>setLeavingBlank(true),
                        "data-testid": Testids.reviewItem.leaveBlankToggle,
                        children: blank.toggle
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-1"
                    }),
                    !blocker && !leavingBlank && /*#__PURE__*/ _jsx(SpecraTooltip, {
                        content: acceptWhyNot || (acknowledge ? 'Records that you saw this; nothing is written' : 'Records your call; it applies on commit'),
                        children: /*#__PURE__*/ _jsx("span", {
                            children: /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                disabled: !acceptReady || !canAct || busy,
                                onClick: ()=>void onAccept(),
                                "data-testid": Testids.reviewItem.acceptButton,
                                children: busy ? 'Saving…' : acceptLabel(decision)
                            })
                        })
                    })
                ]
            })
        ]
    });
}
